// Chute painting (IMPLEMENTATION §0.2 R8/R14, §2.7 PAINT): a stroke is a 4-connected path of cells. Every
// empty cell on it gets a chute, every chute on it is re-directed; corners are automatic (a chute's out side
// points at the next cell, its in side at the previous one). Only the first and the last cell may be a
// building (the stroke starts on a source and ends on a sink): those are skipped. The stroke's ends join
// existing chutes (a chute only takes items from its back, R8):
//   - a stroke that starts on a chute keeps that chute's in side (extending a line round a corner); one that
//     starts on an empty cell a chute points into takes that chute as its back;
//   - a stroke that ends on a chute keeps that chute's out side (merging into the line as a corner); one that
//     ends beside a chute feeds it: straight on if the chute ahead takes from it, else a neighbour chute whose
//     back faces the end, else a neighbour chute nothing feeds from behind, which is turned to take from the
//     end (an extra re-direct step). A building straight ahead (a sink) always wins. Loose items, rock,
// repeated or non-adjacent cells refuse the stroke. New chutes are bought in path order; the unaffordable
// tail is not placed (the stroke ends there). Pure: returns the plan; commandSystem applies it.
import type { RejectReason } from '../commands.ts';
import { DIRS, dirBetween, opposite, stepCell } from '../core/grid.ts';
import type { Cell, Dir, Int } from '../core/types.ts';
import type { SimDefs } from '../defs.ts';
import { findBuilding, findItem } from '../entities.ts';
import { isOpen, type Occ, occAt } from '../occupancy.ts';
import type { Building, ChuteB, SimState } from '../state.ts';
import { DEFAULT_DIR } from './place.ts';
import { priceOf } from './price.ts';

export interface PaintStep {
  x: Int;
  y: Int;
  dir: Dir;
  inDir: Dir;
  /** The chute re-directed here (0 = a new chute). */
  chute: Int;
}

export type PaintPlan =
  | { ok: true; steps: PaintStep[]; price: Int; cost: Int }
  | { ok: false; reason: RejectReason };

/** The building at (x, y), null when empty, an item or rock. */
function buildingOn(s: SimState, occ: Occ, x: Int, y: Int): Building | null {
  if (!isOpen(occ, x, y)) return null;
  const id = occAt(occ, x, y);
  return id > 0 ? findBuilding(s, id) : null;
}

function chuteOn(s: SimState, occ: Occ, x: Int, y: Int): ChuteB | null {
  const b = buildingOn(s, occ, x, y);
  return b !== null && b.type === 'chute' ? b : null;
}

function pointsAt(b: Building, x: Int, y: Int): boolean {
  const f = stepCell(b.x, b.y, b.dir);
  return f.x === x && f.y === y;
}

/** Something behind `ch` can feed it: a chute pointing into it, or any other building. */
function fedFromBack(s: SimState, occ: Occ, ch: ChuteB): boolean {
  const k = stepCell(ch.x, ch.y, ch.inDir);
  const b = buildingOn(s, occ, k.x, k.y);
  return b !== null && (b.type !== 'chute' || pointsAt(b, ch.x, ch.y));
}

/** The in side of a stroke's first cell `p` (going `dir`; `own` = the chute already there, if any). */
function headIn(
  s: SimState,
  occ: Occ,
  p: Cell,
  dir: Dir,
  own: ChuteB | null,
  onPath: boolean[],
): Dir {
  if (own !== null) return own.inDir !== dir ? own.inDir : opposite(dir);
  const straight = opposite(dir);
  const order: Dir[] = [straight];
  for (const d of DIRS) if (d !== straight && d !== dir) order.push(d);
  for (const d of order) {
    const n = stepCell(p.x, p.y, d);
    if (onPath[n.y * occ.cols + n.x] === true) continue;
    const ch = chuteOn(s, occ, n.x, n.y);
    if (ch !== null && pointsAt(ch, p.x, p.y)) return d;
  }
  return straight;
}

/**
 * The out side of a stroke's last cell `p` (entered from `inDir`; `own` = the chute already there) and the
 * neighbour chute re-directed to take from it (null = none).
 */
function tailOut(
  s: SimState,
  occ: Occ,
  p: Cell,
  inDir: Dir,
  own: ChuteB | null,
  onPath: boolean[],
): { dir: Dir; join: PaintStep | null } {
  const straight = opposite(inDir);
  if (own !== null) return { dir: own.dir !== inDir ? own.dir : straight, join: null };
  const ahead = stepCell(p.x, p.y, straight);
  const sink = buildingOn(s, occ, ahead.x, ahead.y);
  if (sink !== null && sink.type !== 'chute') return { dir: straight, join: null };
  const order: Dir[] = [straight];
  for (const d of DIRS) if (d !== straight && d !== inDir) order.push(d);
  const near: { d: Dir; ch: ChuteB }[] = [];
  for (const d of order) {
    const n = stepCell(p.x, p.y, d);
    if (onPath[n.y * occ.cols + n.x] === true) continue;
    const ch = chuteOn(s, occ, n.x, n.y);
    if (ch === null) continue;
    const back = stepCell(ch.x, ch.y, ch.inDir);
    if (back.x === p.x && back.y === p.y) return { dir: d, join: null }; // it already takes from here
    near.push({ d, ch });
  }
  for (const { d, ch } of near) {
    const from = opposite(d);
    if (ch.dir === from || fedFromBack(s, occ, ch)) continue;
    return { dir: d, join: { x: ch.x, y: ch.y, dir: ch.dir, inDir: from, chute: ch.id } };
  }
  return { dir: straight, join: null };
}

export function planPaint(s: SimState, d: SimDefs, occ: Occ, path: readonly Cell[]): PaintPlan {
  if (!Array.isArray(path) || path.length === 0) return { ok: false, reason: 'stale' };
  const last = path.length - 1;
  const seen: boolean[] = [];
  for (let i = 0; i <= last; i++) {
    const p = path[i] as Cell;
    if (typeof p !== 'object' || p === null) return { ok: false, reason: 'stale' };
    if (!isOpen(occ, p.x, p.y)) return { ok: false, reason: 'locked' };
    const ci = p.y * occ.cols + p.x;
    if (seen[ci] === true) return { ok: false, reason: 'stale' };
    seen[ci] = true;
    if (i > 0) {
      const q = path[i - 1] as Cell;
      if (dirBetween(q.x, q.y, p.x, p.y) === null) return { ok: false, reason: 'stale' };
    }
  }
  // every cell must be paintable before anything is bought (an item anywhere refuses the stroke)
  for (let i = 0; i <= last; i++) {
    const p = path[i] as Cell;
    const id = occAt(occ, p.x, p.y);
    if (id <= 0) continue;
    if (findItem(s, id) !== null) return { ok: false, reason: 'occupied' };
    const b = findBuilding(s, id);
    if (b !== null && b.type !== 'chute' && i !== 0 && i !== last) {
      return { ok: false, reason: 'occupied' };
    }
  }
  const price = priceOf(s, d, 'chute') ?? 0;
  const steps: PaintStep[] = [];
  let join: PaintStep | null = null;
  let cash = s.wallet.cash;
  let cost = 0;
  let short = false;
  for (let i = 0; i <= last; i++) {
    const p = path[i] as Cell;
    const id = occAt(occ, p.x, p.y);
    let chute = 0;
    if (id > 0) {
      if (findItem(s, id) !== null) return { ok: false, reason: 'occupied' };
      const b = findBuilding(s, id);
      if (b === null) return { ok: false, reason: 'stale' };
      if (b.type !== 'chute') {
        if (i === 0 || i === last) continue; // stroke ends on a source / sink building
        return { ok: false, reason: 'occupied' };
      }
      chute = b.id;
    }
    const next = i < last ? (path[i + 1] as Cell) : null;
    const prev = i > 0 ? (path[i - 1] as Cell) : null;
    const own = chute > 0 ? chuteOn(s, occ, p.x, p.y) : null;
    let dir: Dir;
    let inDir: Dir;
    if (next !== null) {
      dir = dirBetween(p.x, p.y, next.x, next.y) as Dir;
      inDir =
        prev !== null
          ? (dirBetween(p.x, p.y, prev.x, prev.y) as Dir)
          : headIn(s, occ, p, dir, own, seen);
    } else if (prev !== null) {
      inDir = dirBetween(p.x, p.y, prev.x, prev.y) as Dir;
      const t = tailOut(s, occ, p, inDir, own, seen);
      dir = t.dir;
      join = t.join;
    } else {
      dir = DEFAULT_DIR;
      inDir = opposite(dir);
    }
    if (chute === 0) {
      if (cash < price) {
        short = true; // the unaffordable tail is not placed
        break;
      }
      cash -= price;
      cost += price;
    }
    steps.push({ x: p.x, y: p.y, dir, inDir, chute });
  }
  if (join !== null && !short) steps.push(join);
  if (steps.length === 0) return { ok: false, reason: short ? 'noCash' : 'stale' };
  return { ok: true, steps, price, cost };
}
